// screens-receipt.jsx — Scan a receipt/invoice photo, let the AI extract material
// lines, then import them as report rows (single row or one per material) with markup.
import React from 'react';
import {
  kr, num, fmtMin, fmtHM, STATUS, STATUS_ORDER, ARTICLES, artByCode,
  TEMPLATES, JOBS, VAT, rowAmount, reportTotal, rowLabel, rowUnit, rowRate,
  TODAY, isoLocal, fmtDateLong, fmtDateShort, APP, COMPANY, uid,
  WORK_START_MIN, WORK_END_MIN, hmToMin, minToHM, ceilMin, lunchWindow, lunchAbsorb, groupParts, nextGroupId,
  travelBetween, prevJobSameDay, resolveDeparture,
  Btn, StatusBadge, SecLabel, Card, Avatar, AppHeader, TabBar, TabItem,
  Stepper, Field, Sheet, MapMock, CalendarSheet, Toggle, ProfileButton,
  Icon, ICON_PATHS,
} from '../shared.js';
const { useState: useStateR, useRef: useRefR } = React;

// ── Markup presets (% material påslag) ──────────────────────
const MARKUP_PRESETS = [0, 10, 15, 20];

// Demo fallback — a believable Swedish builders-merchant receipt, used when the
// AI helper is unavailable or can't read the image, so the flow always works.
const DEMO_RECEIPT = {
  vendor: 'Ahlsell Göteborg',
  date: TODAY,
  currency: 'SEK',
  items: [
    { name: 'Kopparrör 15 mm', qty: 6, unit: 'm', unitPrice: 48 },
    { name: 'Rörkoppling T 15 mm', qty: 8, unit: 'st', unitPrice: 29 },
    { name: 'Avstängningsventil 15 mm', qty: 2, unit: 'st', unitPrice: 119 },
    { name: 'Lödtenn 99C 250 g', qty: 1, unit: 'rle', unitPrice: 215 },
  ],
  total: 6 * 48 + 8 * 29 + 2 * 119 + 215,
  _demo: true,
};

function parseReceiptJSON(text) {
  if (!text) throw new Error('tomt svar');
  let t = String(text).trim();
  // strip ``` fences
  t = t.replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
  const a = t.indexOf('{'), b = t.lastIndexOf('}');
  if (a !== -1 && b !== -1) t = t.slice(a, b + 1);
  const data = JSON.parse(t);
  const items = Array.isArray(data.items) ? data.items.map(it => ({
    name: String(it.name || 'Artikel'),
    qty: Number(it.qty) > 0 ? Number(it.qty) : 1,
    unit: String(it.unit || 'st'),
    unitPrice: Math.max(0, Math.round(Number(it.unitPrice) || 0)),
  })) : [];
  if (!items.length) throw new Error('inga rader');
  const sum = items.reduce((s, it) => s + it.qty * it.unitPrice, 0);
  return {
    vendor: data.vendor ? String(data.vendor) : 'Okänd leverantör',
    date: /^\d{4}-\d{2}-\d{2}$/.test(data.date) ? data.date : null,
    currency: data.currency || 'SEK',
    items,
    total: Math.round(Number(data.total) || sum),
  };
}

async function analyzeReceipt(dataUrl) {
  const m = /^data:(image\/[\w.+-]+);base64,(.+)$/.exec(dataUrl || '');
  if (!m) throw new Error('ogiltig bild');
  if (!(window.claude && typeof window.claude.complete === 'function')) {
    throw new Error('AI ej tillgänglig');
  }
  const prompt =
    'You parse receipts/invoices for a Swedish contractor (VVS/el/bygg) app. ' +
    'Read the attached image and extract the purchased line items. ' +
    'Return ONLY valid minified JSON, no prose, with this exact shape:\n' +
    '{"vendor":string,"date":"YYYY-MM-DD"|null,"currency":"SEK","items":[{"name":string,"qty":number,"unit":string,"unitPrice":number}],"total":number}\n' +
    '- name: the article text in Swedish exactly as printed.\n' +
    '- unit: short unit like "st","m","kg","l","pkt","rle".\n' +
    '- unitPrice: price per unit excluding VAT if shown, otherwise as printed (number only, SEK).\n' +
    '- total: grand total excluding VAT if shown, else the printed total.\n' +
    'Do your best even if the image is blurry. No comments, no markdown.';
  const resp = await window.claude.complete({
    messages: [{
      role: 'user',
      content: [
        { type: 'image', source: { type: 'base64', media_type: m[1], data: m[2] } },
        { type: 'text', text: prompt },
      ],
    }],
  });
  return parseReceiptJSON(resp);
}

// ── Build report rows from a parsed receipt ─────────────────
function receiptToRows(result, mode, markupPct) {
  const f = 1 + (markupPct || 0) / 100;
  if (mode === 'single') {
    const base = result.total || result.items.reduce((s, it) => s + it.qty * it.unitPrice, 0);
    return [{ id: uid('r'), code: 'MTRL', qty: 1, rate: Math.round(base * f), label: `Material – ${result.vendor}`, note: '' }];
  }
  return result.items.map(it => ({
    id: uid('r'), code: 'MTRL', qty: it.qty, rate: Math.round(it.unitPrice * f), label: it.name, note: '',
  }));
}

// ── The scan sheet ──────────────────────────────────────────
function ReceiptScanSheet({ open, onClose, onImport }) {
  const [stage, setStage] = useStateR('capture'); // capture | analyzing | review | error
  const [img, setImg] = useStateR(null);
  const [result, setResult] = useStateR(null);
  const [mode, setMode] = useStateR('multi'); // multi | single
  const [markup, setMarkup] = useStateR(0);
  const [errMsg, setErrMsg] = useStateR('');
  const fileRef = useRefR(null);

  function reset() {
    setStage('capture'); setImg(null); setResult(null);
    setMode('multi'); setMarkup(0); setErrMsg('');
  }
  function close() { onClose(); setTimeout(reset, 320); }

  function onFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { const url = reader.result; setImg(url); run(url); };
    reader.readAsDataURL(file);
  }

  async function run(url) {
    setStage('analyzing'); setErrMsg('');
    try {
      const r = await analyzeReceipt(url);
      setResult(r); setStage('review');
    } catch (err) {
      // graceful fallback so the prototype flow is always usable
      setErrMsg(err && err.message ? err.message : 'okänt fel');
      setResult({ ...DEMO_RECEIPT });
      setStage('review');
    }
  }

  // demo entry without a real photo
  function runDemo() { setImg(null); setResult({ ...DEMO_RECEIPT }); setMarkup(0); setMode('multi'); setStage('review'); }

  const f = 1 + markup / 100;
  const baseSum = result ? result.items.reduce((s, it) => s + it.qty * it.unitPrice, 0) : 0;
  const baseTotal = result ? (result.total || baseSum) : 0;
  const importTotal = mode === 'single' ? Math.round(baseTotal * f) : result ? result.items.reduce((s, it) => s + it.qty * Math.round(it.unitPrice * f), 0) : 0;

  const patchItem = (i, p) => setResult(r => ({ ...r, items: r.items.map((it, j) => j === i ? { ...it, ...p } : it) }));
  const removeItem = (i) => setResult(r => ({ ...r, items: r.items.filter((_, j) => j !== i) }));

  return (
    <Sheet open={open} onClose={close} title={stage === 'review' ? 'Importera kvitto' : 'Skanna kvitto'} maxH="92%">
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} style={{ display: 'none' }} />

      {/* ── CAPTURE ─────────────────────────────────── */}
      {stage === 'capture' && (
        <div className="gv-fade" style={{ paddingBottom: 6 }}>
          <div className="gv-drop">
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--accent-wash)', color: 'var(--accent-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <Icon name="receipt" size={28} stroke={1.8} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 740 }}>Fota kvitto eller faktura</div>
            <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.45, maxWidth: 260 }}>
              AI läser av leverantör och materialrader och fyller i rapporten åt dig.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <Btn variant="primary" icon="camera" onClick={() => fileRef.current && fileRef.current.click()} style={{ flex: 1 }}>Ta foto</Btn>
            <Btn variant="secondary" icon="image" onClick={() => fileRef.current && fileRef.current.click()} style={{ flex: 1 }}>Galleri</Btn>
          </div>
          <button onClick={runDemo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', marginTop: 14, background: 'none', border: 'none', color: 'var(--ink-3)', fontSize: 12.5, fontWeight: 600 }}>
            <Icon name="sparkle" size={14} stroke={1.9} /> Prova med exempelkvitto
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 14, color: 'var(--ink-3)', fontSize: 12, lineHeight: 1.4 }}>
            <Icon name="scan" size={14} stroke={1.9} /> Bilden analyseras direkt — inget skickas till bokföringen förrän du importerar.
          </div>
        </div>
      )}

      {/* ── ANALYZING ───────────────────────────────── */}
      {stage === 'analyzing' && (
        <div className="gv-fade" style={{ padding: '26px 4px 30px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16 }}>
          {img && <img src={img} alt="" className="gv-rcpt-thumb" style={{ height: 150 }} />}
          <div style={{ position: 'relative', width: 46, height: 46 }}>
            <div className="gv-spin" style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid var(--accent-wash)', borderTopColor: 'var(--accent)' }} />
            <Icon name="sparkle" size={20} stroke={2} color="var(--accent-ink)" style={{ position: 'absolute', inset: 13 }} />
          </div>
          <div>
            <div style={{ fontSize: 15.5, fontWeight: 720 }}>Analyserar kvitto…</div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2, animation: 'gvpulse 1.4s ease-in-out infinite' }}>Läser leverantör, artiklar och belopp</div>
          </div>
        </div>
      )}

      {/* ── REVIEW ──────────────────────────────────── */}
      {stage === 'review' && result && (
        <div className="gv-fade" style={{ paddingBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            {img
              ? <img src={img} alt="" style={{ width: 52, height: 52, borderRadius: 12, objectFit: 'cover', border: '1px solid var(--line)', flexShrink: 0 }} />
              : <div style={{ width: 52, height: 52, borderRadius: 12, background: 'var(--accent-wash)', color: 'var(--accent-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="receipt" size={24} stroke={1.8} /></div>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 740, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{result.vendor}</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{result.date ? fmtDateShort(result.date) : 'Datum saknas'} · {result.items.length} {result.items.length === 1 ? 'rad' : 'rader'}</div>
            </div>
            <span className="gv-badge is-sm" style={{ background: result._demo ? 'var(--surface-3)' : 'var(--accent-wash)', color: result._demo ? 'var(--ink-2)' : 'var(--accent-ink)' }}>
              <Icon name="sparkle" size={11} stroke={2} /> {result._demo ? 'Exempel' : 'AI-avläst'}
            </span>
          </div>

          {errMsg && !result._demo && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>Kunde inte nå AI ({errMsg}) — visar exempel. Justera raderna nedan.</div>
          )}
          {result._demo && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>Exempeldata — fota ett riktigt kvitto för automatisk avläsning.</div>
          )}

          {/* mode toggle */}
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--ink-3)', marginBottom: 8 }}>Importera som</div>
          <div style={{ display: 'flex', gap: 7, background: 'var(--surface-2)', padding: 4, borderRadius: 12, marginBottom: 16 }}>
            {[['multi', 'En rad per material'], ['single', 'Allt som en rad']].map(([k, l]) => (
              <button key={k} onClick={() => setMode(k)} style={{ flex: 1, height: 38, borderRadius: 9, border: 'none', fontWeight: 700, fontSize: 13, background: mode === k ? 'var(--surface)' : 'transparent', color: mode === k ? 'var(--ink)' : 'var(--ink-3)', boxShadow: mode === k ? '0 1px 3px rgba(0,0,0,.08)' : 'none' }}>{l}</button>
            ))}
          </div>

          {/* markup */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--ink-3)' }}>Påslag på material</span>
            <Stepper value={markup} onChange={(v) => setMarkup(Math.max(0, v))} step={5} min={0} size="sm" />
          </div>
          <div style={{ display: 'flex', gap: 7, marginBottom: 16 }}>
            {MARKUP_PRESETS.map(p => (
              <button key={p} onClick={() => setMarkup(p)} className={'gv-chip' + (markup === p ? ' is-on' : '')} style={{ flex: 1, justifyContent: 'center', height: 36 }}>{p}%</button>
            ))}
          </div>

          {/* items */}
          {mode === 'multi' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 4 }}>
              {result.items.map((it, i) => {
                const marked = Math.round(it.unitPrice * f);
                return (
                  <Card key={i} style={{ padding: '9px 11px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <div className="gv-lrow-ic" style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0 }}><Icon name="box" size={16} stroke={1.9} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <input className="gv-input" style={{ padding: 0, fontSize: 14, fontWeight: 650 }} value={it.name} onChange={e => patchItem(i, { name: e.target.value })} />
                        <div className="gv-mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
                          à<input className="is-mono" style={{ width: 38, border: 'none', borderBottom: '1px solid var(--line-2)', background: 'none', outline: 'none', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-2)', textAlign: 'right', padding: '0 1px 1px' }} inputMode="numeric" value={it.unitPrice} onChange={e => patchItem(i, { unitPrice: parseInt(e.target.value.replace(/\D/g, '') || '0', 10) })} />kr/{it.unit}
                          {markup > 0 && <span style={{ color: 'var(--accent-ink)' }}>→ {marked} kr</span>}
                        </div>
                      </div>
                      <Stepper value={it.qty} onChange={v => patchItem(i, { qty: v })} step={1} min={0} size="sm" />
                      <span className="gv-tnum" style={{ fontSize: 13.5, fontWeight: 760, minWidth: 50, textAlign: 'right', flexShrink: 0 }}>{kr(it.qty * marked)}</span>
                      <button className="gv-iconbtn is-soft" style={{ width: 28, height: 28, flexShrink: 0 }} onClick={() => removeItem(i)} aria-label="Ta bort"><Icon name="trash" size={15} stroke={1.9} color="var(--ink-3)" /></button>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="gv-lrow-ic" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}><Icon name="box" size={18} stroke={1.9} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 700 }}>Material – {result.vendor}</div>
                <div className="gv-mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{result.items.length} artiklar slås ihop till en rad</div>
              </div>
            </Card>
          )}

          {/* total to import */}
          <Card style={{ marginTop: 16, padding: 0, overflow: 'hidden' }}>
            {markup > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 16px' }}>
                <span style={{ fontSize: 13.5, color: 'var(--ink-3)' }}>Materialkostnad</span>
                <span className="gv-tnum" style={{ fontSize: 14, color: 'var(--ink-2)' }}>{kr(baseTotal)}</span>
              </div>
            )}
            {markup > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px 11px' }}>
                <span style={{ fontSize: 13.5, color: 'var(--ink-3)' }}>Påslag {markup}%</span>
                <span className="gv-tnum" style={{ fontSize: 14, color: 'var(--accent-ink)', fontWeight: 700 }}>+ {kr(importTotal - baseTotal)}</span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', background: 'var(--accent-wash)' }}>
              <span style={{ fontSize: 15, fontWeight: 760 }}>Att lägga till</span>
              <span className="gv-tnum" style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-ink)' }}>{kr(importTotal)}</span>
            </div>
          </Card>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <Btn variant="secondary" icon="back" onClick={reset} style={{ flex: 1 }}>Skanna om</Btn>
            <Btn variant="primary" icon="plusCircle" disabled={mode === 'multi' && result.items.length === 0} onClick={() => { onImport(receiptToRows(result, mode, markup)); close(); }} style={{ flex: 1.6 }}>
              {mode === 'single' ? 'Lägg till 1 rad' : `Lägg till ${result.items.length} ${result.items.length === 1 ? 'rad' : 'rader'}`}
            </Btn>
          </div>
        </div>
      )}
    </Sheet>
  );
}

export { ReceiptScanSheet, analyzeReceipt, receiptToRows, parseReceiptJSON };
